/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */

export type PercentParams = {
  first: number | number[];
  second: number | number[];
  round?: boolean;
  display?: boolean;
};

/**
 * Provides a simple utility for measuring the difference in time between two events.
 *
 * ```typescript
 *
 *   import Metrics from '@ion/core/src/performance/Metrics';
 *   import {info} from '@ion/core/src/logger/logger';
 *
 *    // instantiate a metric instance with autostart true ...
 *   const metrics = new Metrics(true);
 *
 *   // wait some time, e.g after an async call completes, etc.
 *   metrics.stop();
 *   info(metrics.results());
 *
 *   // run and stop multiple measurements ...
 *   const metrics = new Metrics();
 *
 *   metrics.start(); // measurements are running
 *   metrics.isRunning(); // true
 *   metrics.stop(); // measurements have completed
 *   info(metrics.results()); // measurement results
 *
 *   // run next set ...
 *   metrics.restart();
 *   info(metrics.results()); // measurement results (auto-stop true by default)
 *
 *   // method chaining ...
 *   const metrics = new Metrics();
 *   info(metrics.start().isRunning()); // true
 *   info(metrics.stop().results()); // measurement results
 *
 * ```
 *
 * @class Metrics
 */
export default class Metrics {
  /**
   * @private
   */
  _begin: Date;

  /**
   * @private
   */
  _end: Date;

  /**
   * @constructor
   *
   * @param  {boolean} autostart When true, immediately begins running a measurement
   *         (defaults to false).
   */
  constructor(autostart = false) {
    if (autostart) {
      this._begin = new Date();
    }
  }

  /**
   * Starts the measurements if not started during instantiation.
   *
   * @method start
   * @returns {Metrics} a reference to this Metrics instance.
   */
  start(): Metrics {
    if (!this._begin) {
      this._begin = new Date();
    }
    return this;
  }

  /**
   * Stops the measurements if already started, either during instantiation or via the
   * start method.
   *
   * @method start
   * @returns {Metrics} a reference to this Metrics instance.
   */
  stop(): Metrics {
    if (this._begin) {
      this._end = new Date();
    }
    return this;
  }

  /**
   * Returns the results of the operations in seconds, i.e. the difference between the
   * start and stop calls.
   *
   * @method  results
   * @param   {boolean} autostop When true (default), allows for calling the results method
   *          without having to explicitly call the stop method, i.e. calling results with
   *          autostop true immediately stops the current measurement and returns the results.
   * @param   {boolean} round When true, the results will be rounded.
   * @returns {number} results of the operations in seconds.
   */
  results(autostop = true, round = true): number {
    if (this._begin) {
      !this._end && autostop && this.stop();
      const end = Number(this._end);
      const begin = Number(this._begin);
      const result = ((end - begin) / 1000) as number;
      return round ? Math.round(result) : result;
    }
  }

  /**
   * Determines if the metrics API is running, returning true if running, otherwise false.
   *
   * @method isRunning
   * @returns {boolean} True if measurements are currently being taken, otherwise, false.
   */
  isRunning(): boolean {
    return Boolean(this._begin && !this._end);
  }

  /**
   * Restarts the metrics for a new run. This method is essentially the equivalent of
   * instantiating a new instance with the autostart argument as true, or calling the
   * start method for the first time.
   *
   * @method start
   * @returns {Metrics} a reference to this Metrics instance.
   */
  restart(): Metrics {
    this._begin = new Date();
    this._end   = null;
    return this;
  }

  /**
   * Resets the metrics instance to its initial, un-started default state.
   *
   * @method start
   * @returns {Metrics} a reference to this Metrics instance.
   */
  reset(): Metrics {
    this._begin = this._end = null;
    return this;
  }

  /**
   * Returns the average from a set of sampled values.
   *
   * ```typescript
   *
   *   import Metrics from '@ion/core/src/capabilities';
   *
   *   const a = [10.2, 33.3, 45];
   *   const Metrics.average(a); // 29.5
   *   const Metrics.average([10.2, 33.3, 45], true); // 30
   *
   * ```
   *
   * @static
   * @method  average
   *
   * @param   {array} values A set of numeric values.
   * @param   {boolean} round When true, the result will be rounded.
   * @returns {number} The average value from the set.
   */
  static average(values: number[], round = false): number {
    let result = 0;
    values.forEach(value => (result += value));
    const avg = result / values.length;
    return round ? Math.round(avg) : avg;
  }

  /**
   * Returns the relative percentage decrease or increase between two sets of values,
   * or two values. When the first and or second arguments are arrays, the average
   * method will be invoked against each to obtain the average of each set.
   *
   * The result can be rounded and / or returned as a percentage string.
   *
   * ```typescript
   *
   *   import Metrics from '@ion/core/src/capabilities';
   *
   *   // determine percent decrease from two sets of values ...
   *   const a = [10.2, 33.3, 45];
   *   const b = [6.2, 3.1, 9.6];
   *
   *   const Metrics.percent(a, b); // 78.64406779661016
   *   const Metrics.percent(a, b, true); // 79
   *   const Metrics.percent(a, b, false, true); // "78.64406779661016%"
   *   const Metrics.percent(a, b, true, true); // "79%"
   *
   * ```
   *
   *
   * @static
   * @method  percent
   *
   * @param   {array, number} first The initial set of values, or value
   * @param   {array, number} second The resulting set of values, or value
   * @param   {boolean} round When true, the values will be rounded
   * @param   {boolean} display When true, a percentage string will be returned
   * @returns {number, string} The percentage decrease between the two sets
   */
  static percent({first, second, round = false, display = false}: PercentParams): number | string {
    const a: number = Array.isArray(first) ? Metrics.average(first) : first;
    const b: number = Array.isArray(second) ? Metrics.average(second) : second;
    const d: number = Math.abs(((a - b) / a)) * 100;
    const r: number = round ? Math.round(d) : d;
    return display ? `${r}%` : r;
  }
}
